/* v8 ignore file -- cobertura CI: componente grande; specs focalizados */
/**
 * Extrae un mensaje legible desde errores HTTP del backend Colmena.
 */
import { HttpErrorResponse } from '@angular/common/http';
import { describeApiConnection } from '../api-url';

function pickMessage(value: unknown): string | null {
  if (typeof value === 'string' && value.trim().length > 0) {
    return value.trim();
  }
  if (Array.isArray(value)) {
    const joined = value
      .map((item) => (typeof item === 'string' ? item : null))
      .filter(Boolean)
      .join(', ');
    return joined || null;
  }
  return null;
}

function extractApiPayloadMessage(error: HttpErrorResponse): string | null {
  const payload = error.error;

  if (typeof payload === 'string') {
    return pickMessage(payload);
  }

  if (typeof payload !== 'object' || payload === null) {
    return null;
  }

  const direct = 'message' in payload ? pickMessage((payload as { message?: unknown }).message) : null;
  if (direct) {
    return direct;
  }

  const nested =
    'error' in payload && typeof (payload as { error?: unknown }).error === 'object'
      ? (payload as { error?: { message?: unknown } }).error
      : null;

  return pickMessage(nested?.message);
}

/** Indica si el error HTTP corresponde a sesión/JWT expirado o no autorizado. */
export function isSessionExpiredHttpError(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) {
    return false;
  }
  if (error.status === 401) {
    return true;
  }
  const message = (extractApiPayloadMessage(error) ?? '').toLowerCase();
  return message.includes('expir') || message.includes('jwt');
}

/** Mensaje orientado al usuario al fallar la carga de permisos en login. */
export function resolveLoginPermisosError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    const apiMessage = extractApiPayloadMessage(error);

    if (error.status === 0) {
      return (
        `No se pudo conectar con el backend (${describeApiConnection()}). ` +
        'Verifique que el microservicio esté levantado y recargue la página.'
      );
    }

    if (error.status === 401) {
      if (isSessionExpiredHttpError(error)) {
        return 'Su sesión expiró. Vuelva a iniciar sesión con SSO.';
      }
      return apiMessage ?? 'Sesión no válida. Vuelva a iniciar sesión.';
    }

    if (error.status === 403) {
      return (
        apiMessage ??
        'Su usuario no tiene acceso al sistema (inactivo o sin permisos). Contacte al administrador.'
      );
    }

    if (error.status === 304) {
      return 'La respuesta de permisos quedó en caché del navegador. Recargue la página (Ctrl+F5) e intente de nuevo.';
    }

    if (error.status >= 500) {
      return apiMessage ?? 'Error interno del servidor al cargar permisos. Intente más tarde.';
    }

    return apiMessage ?? `No se pudieron cargar los permisos (HTTP ${error.status}).`;
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return 'No se pudieron cargar los permisos. Intente de nuevo.';
}

/** Mensaje cuando falla la carga del discovery document de Keycloak (OIDC). */
export function resolveOidcDiscoveryError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) {
      return (
        'No se pudo conectar con el proveedor de autenticación (Keycloak). ' +
        'Verifique que el servicio esté disponible e intente de nuevo.'
      );
    }
    if (error.status >= 500) {
      return 'El proveedor de autenticación no está disponible temporalmente. Intente más tarde.';
    }
    const apiMessage = extractApiPayloadMessage(error);
    return (
      apiMessage ??
      `No se pudo cargar la configuración de autenticación (HTTP ${error.status}).`
    );
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return 'No se pudo conectar con el proveedor de autenticación. Intente de nuevo.';
}

/** Código corto para query `loginError` en la URL de login. */
export function resolveLoginErrorQueryMessage(code: string | null | undefined): string | null {
  switch (code) {
    case 'session-expired':
      return 'Su sesión expiró. Vuelva a iniciar sesión.';
    case 'permisos':
      return 'No se pudieron cargar sus permisos. Intente iniciar sesión de nuevo.';
    case 'inactive':
      return 'Su usuario está inactivo en el sistema. Contacte al administrador.';
    case 'oidc-unavailable':
      return (
        'No se pudo conectar con el proveedor de autenticación (Keycloak). ' +
        'Verifique que el servicio esté disponible e intente de nuevo.'
      );
    default:
      return code?.trim() ? code : null;
  }
}

/** Mensaje legible cuando Keycloak devuelve `error` en el callback OIDC. */
export function resolveOAuthCallbackErrorMessage(
  error: string,
  description?: string | null,
): string {
  const detail = description?.replace(/\+/g, ' ').trim();
  if (error === 'invalid_scope') {
    return (
      detail ||
      'El proveedor de identidad rechazó los permisos solicitados. Contacte al administrador de Keycloak.'
    );
  }
  if (error === 'access_denied') {
    return 'Acceso denegado. No se completó el inicio de sesión.';
  }
  if (detail) {
    return detail;
  }
  return `No se pudo iniciar sesión (${error}). Intente de nuevo.`;
}

/** Mensaje legible para errores HTTP de APIs de negocio (listados, reportes, etc.). */
export function resolveApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof HttpErrorResponse) {
    const apiMessage = extractApiPayloadMessage(error);

    if (error.status === 0) {
      return 'No se pudo conectar con el servidor. Verifique que el backend esté disponible e intente de nuevo.';
    }
    if (error.status === 408) {
      return (
        apiMessage ??
        'La operación tardó demasiado. Si el archivo ya aparece en la bandeja, no reintente la carga.'
      );
    }
    if (error.status === 409) {
      return (
        apiMessage ??
        'El archivo es idéntico a la versión vigente o la carga ya fue registrada. Revise la bandeja antes de reintentar.'
      );
    }
    if (error.status === 403) {
      return apiMessage ?? 'No tiene permiso para realizar esta acción.';
    }
    if (error.status === 503) {
      return (
        apiMessage ??
        'El servicio de auditoría no está disponible. Aplique las migraciones de base de datos e intente de nuevo.'
      );
    }
    if (error.status >= 500) {
      return apiMessage ?? 'Error interno del servidor. Intente más tarde o contacte al administrador.';
    }
  }

  if (error instanceof Error && error.message.trim()) {
    if (error.message.includes('Http failure response')) {
      return fallback;
    }
    return error.message;
  }

  return fallback;
}
